import {
  TooltipTrigger,
  TooltipContent,
  FormSwitch,
  Typography,
  FormSelect,
  Tooltip,
  Label,
} from '@ui/components';
import { useGetCostCenters, useHasPermission } from '@shared/hooks';
import { CostCenterFormType } from '@pages/cost-centers/utils';
import { useAccounts } from '@pages/cost-centers/hooks';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, FC } from 'react';
import { Info } from '@ui/components/icons';

export const CostCenterSettingsForm: FC<{
  form: CostCenterFormType;
  costCenterId?: string;
  editMode?: boolean;
}> = ({ costCenterId, editMode, form }) => {
  const hasEditPermission = useHasPermission('COST_CENTER_EDIT_COST_CENTER_DEFAULT', 'WRITE');
  const { t } = useTranslation();
  const { accountNumbersOptions } = useAccounts();
  const { costCentersOptions } = useGetCostCenters({
    disable: !editMode,
  });

  const useExistingTechnicalAccountNumber = form.watch('useExistingTechnicalAccountNumber');
  const wasInitiallyDefault = form.watch('wasInitiallyDefault');
  const wasInitiallyAutomaticAssignment = form.watch('wasInitiallyAutomaticAssignment');
  const defaultCostCenter = form.watch('defaultCostCenter');
  const automaticAssignment = form.watch('automaticAssignment');

  const showNewDefaultCostCenterControl = !!editMode && wasInitiallyDefault && !defaultCostCenter;
  const showAutomaticAssignmentControl =
    !!editMode && wasInitiallyAutomaticAssignment && !automaticAssignment;

  const defaultCostCentersOptions = useMemo(() => {
    return costCentersOptions.filter((costCenter) => costCenter.value !== costCenterId);
  }, [costCentersOptions, costCenterId]);

  const defaultCostCenterDisabled = useMemo(() => {
    return editMode && defaultCostCenter && defaultCostCentersOptions.length < 1;
  }, [defaultCostCentersOptions, editMode, defaultCostCenter]);

  const automaticAssignmentDisabled = useMemo(() => {
    return editMode && automaticAssignment && defaultCostCentersOptions.length < 1;
  }, [defaultCostCentersOptions, editMode, automaticAssignment]);

  useEffect(() => {
    if (editMode && defaultCostCentersOptions.length < 2 && !!defaultCostCentersOptions[0]) {
      form.setValue('newDefaultCostCenterId', defaultCostCentersOptions[0].value);
      form.setValue('newAutomaticAssignmentCostCenterId', defaultCostCentersOptions[0].value);
    }
  }, [defaultCostCentersOptions, form, editMode]);

  return (
    <div className="flex flex-col gap-2">
      <Typography variant="labelMediumBold">{t('COST_CENTERS.SETTINGS')}</Typography>

      <div className="grid grid-cols-6 gap-4 items-center">
        <Label className="col-span-5 flex items-center gap-2" htmlFor="defaultCostCenter">
          <Typography variant="labelSmallBook" element="span">
            {t('COST_CENTERS.DEFAULT_ACCOUNT')}
          </Typography>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="text-weak" />
            </TooltipTrigger>
            <TooltipContent>{t('COST_CENTERS.DEFAULT_ACCOUNT_TOOLTIP')}</TooltipContent>
          </Tooltip>
        </Label>

        {/* TODO: This should be disabled for non-OWNER roles */}
        <div className="col-span-1  flex justify-end">
          <FormSwitch
            disabled={defaultCostCenterDisabled || !hasEditPermission}
            formControl={form.control}
            name="defaultCostCenter"
          />
        </div>

        {showNewDefaultCostCenterControl && (
          <>
            <Label className="col-span-2 flex items-center gap-2" htmlFor="newDefaultCostCenterId">
              <Typography variant="labelSmallBook" element="label">
                {t('COST_CENTERS.NEW_DEFAULT_ACCOUNT')}
              </Typography>
            </Label>
            <FormSelect
              disabled={defaultCostCentersOptions.length < 2 || !hasEditPermission}
              placeholder={t('COST_CENTERS.NEW_DEFAULT_ACCOUNT')}
              options={defaultCostCentersOptions}
              name="newDefaultCostCenterId"
              formControl={form.control}
              className="col-span-4"
            />
          </>
        )}

        <Label className="col-span-5 flex items-center gap-2" htmlFor="automaticAssignment">
          <Typography variant="labelSmallBook" element="span">
            {t('COST_CENTERS.AUTOMATIC_ASSIGNMENT')}
          </Typography>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="text-weak" />
            </TooltipTrigger>
            <TooltipContent>{t('COST_CENTERS.AUTOMATIC_ASSIGNMENT_TOOLTIP')}</TooltipContent>
          </Tooltip>
        </Label>

        {/* TODO: This should be disabled for non-OWNER roles */}
        <div className="col-span-1  flex justify-end">
          <FormSwitch
            disabled={automaticAssignmentDisabled || !hasEditPermission}
            formControl={form.control}
            name="automaticAssignment"
          />
        </div>

        {showAutomaticAssignmentControl && (
          <>
            <Label
              className="col-span-2 flex items-center gap-2"
              htmlFor="newAutomaticAssignmentCostCenterId"
            >
              <Typography variant="labelSmallBook" element="label">
                {t('COST_CENTERS.NEW_AUTOMATIC_ASSIGNMENT')}
              </Typography>
            </Label>
            <FormSelect
              disabled={defaultCostCentersOptions.length < 2 || !hasEditPermission}
              placeholder={t('COST_CENTERS.NEW_AUTOMATIC_ASSIGNMENT')}
              name="newAutomaticAssignmentCostCenterId"
              options={defaultCostCentersOptions}
              formControl={form.control}
              className="col-span-4"
            />
          </>
        )}

        {!editMode && (
          <>
            <Label htmlFor="useExistingTechnicalAccountNumber" className="col-span-5">
              <Typography variant="labelSmallBook">
                {t('COST_CENTERS.USE_EXISTING_TECHNICAL_ACCOUNT_NUMBER')}
              </Typography>
            </Label>

            <div className="col-span-1 flex justify-end">
              <FormSwitch name="useExistingTechnicalAccountNumber" formControl={form.control} />
            </div>
            <div className="col-span-6">
              <Typography variant="labelSmallBook" className="text-weak">
                {t('COST_CENTERS.USE_EXISTING_TECHNICAL_ACCOUNT_NUMBER_MESSAGE')}
              </Typography>
            </div>

            {!!useExistingTechnicalAccountNumber && (
              <>
                <Label htmlFor="technicalAccountId" className="col-span-2">
                  <Typography variant="labelSmallBook" className="text-weak">
                    {t('COST_CENTERS.ACCOUNT_NUMBER')}
                  </Typography>
                </Label>

                <div className="col-span-4">
                  <FormSelect
                    placeholder={t('COST_CENTERS.ACCOUNT_NUMBER')}
                    options={accountNumbersOptions}
                    disabled={!hasEditPermission}
                    formControl={form.control}
                    name="technicalAccountId"
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
